import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OfferDto, OfferTrigger } from '@rw/shared';
import { useState } from 'react';
import { Sheet } from '../../components/Sheet';
import { LoadingState } from '../../components/states';
import { Button, Card } from '../../components/ui';
import { useModerationEvents } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { t as tr, useT } from '../../i18n';
import { toast } from '../../stores/toasts';
import { useCampaigns } from '../campaigns/campaigns-api';
import { offerKeys, useOffers } from '../offers/offers-api';
import { BrandLogo } from './BrandsTab';
import { useBrands } from './brands-api';

const input = 'mt-1 h-11 w-full rounded-xl border border-ink-700 bg-ink-850 px-3 outline-none focus:border-volt';
const TRIGGER_LABEL: Record<OfferTrigger, string> = {
  CAMPAIGN_CELL: 'Capture a sponsored cell of a campaign',
  EVENT_FINISH: 'Finish an event (qualified, with a counted run)',
  EVENT_PODIUM: 'Top 3 of an event',
};
const STATUS: Record<OfferDto['status'], { label: string; cls: string }> = {
  ACTIVE: { label: 'Live', cls: 'bg-volt/15 text-volt' },
  PAUSED: { label: 'Paused', cls: 'bg-gold/15 text-gold' },
  EXPIRED: { label: 'Expired', cls: 'bg-ink-700 text-ink-300' },
  OUT_OF_CODES: { label: 'Out of codes', cls: 'bg-enemy/15 text-enemy' },
  NO_CODES: { label: 'No codes yet', cls: 'bg-enemy/15 text-enemy' },
};
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/** Game owner: brand offers — a pool of codes, one per runner who earns it. */
export function OffersTab() {
  const q = useOffers();
  const t = useT();
  const [editing, setEditing] = useState<OfferDto | 'new' | null>(null);
  const [codesFor, setCodesFor] = useState<OfferDto | null>(null);
  if (!q.data) return <LoadingState />;
  return (
    <div className="mt-4 space-y-3" data-testid="admin-offers">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-400">{t('Codes from a CSV, one per runner who captures a campaign cell, finishes an event or makes its podium.')}</p>
        <Button size="sm" className="shrink-0 whitespace-nowrap" onClick={() => setEditing('new')} data-testid="offer-new">
          {t('+ New')}
        </Button>
      </div>
      {q.data.length === 0 ? <p className="text-sm text-ink-500">{t('No offers yet.')}</p> : null}
      {q.data.map((o) => (
        <div key={o.id} data-testid="offer-row">
          <Card className="space-y-2">
            <div className="flex items-center gap-3">
              <BrandLogo brand={o.brand} className="h-10 w-16" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">{o.title}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS[o.status].cls}`}>{t(STATUS[o.status].label)}</span>
                </div>
                <div className="truncate text-xs text-ink-400">
                  {t('{trigger}: {source} · until {date}', { trigger: t(TRIGGER_LABEL[o.trigger]), source: o.source?.name ?? '—', date: formatDate(o.expiresAt) })}
                </div>
              </div>
            </div>
            <div className="text-xs text-ink-300" data-testid="offer-codes">
              {t('{claimed} / {total} codes given · {used} used', { claimed: o.codes.claimed, total: o.codes.total, used: o.codes.used })}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" className="flex-1" onClick={() => setEditing(o)}>
                {t('Edit')}
              </Button>
              <Button size="sm" variant="secondary" className="flex-1" onClick={() => setCodesFor(o)} data-testid={`offer-add-codes-${o.title}`}>
                {t('Codes')}
              </Button>
            </div>
          </Card>
        </div>
      ))}
      <OfferSheet offer={editing} onClose={() => setEditing(null)} onCreated={setCodesFor} />
      <CodesSheet offer={codesFor} onClose={() => setCodesFor(null)} />
    </div>
  );
}

function OfferSheet({ offer, onClose, onCreated }: { offer: OfferDto | 'new' | null; onClose: () => void; onCreated: (o: OfferDto) => void }) {
  const qc = useQueryClient();
  const t = useT();
  const brands = useBrands(!!offer);
  const campaigns = useCampaigns();
  const events = useModerationEvents();
  const existing = offer && offer !== 'new' ? offer : null;
  const [form, setForm] = useState<Record<string, string>>({});
  const [paused, setPaused] = useState(false);
  const key = existing?.id ?? 'new';
  const [formKey, setFormKey] = useState<string | null>(null);
  if (offer && formKey !== key) {
    setFormKey(key);
    setForm({
      brandId: existing?.brand.id ?? '',
      title: existing?.title ?? '',
      body: existing?.body ?? '',
      terms: existing?.terms ?? '',
      trigger: existing?.trigger ?? 'CAMPAIGN_CELL',
      sourceId: existing?.source?.id ?? '',
      expiresAt: toLocalInput(existing?.expiresAt ?? new Date(Date.now() + 30 * 86_400_000).toISOString()),
    });
    setPaused(existing?.paused ?? false);
  }
  const close = () => {
    setFormKey(null);
    onClose();
  };
  const save = useMutation({
    mutationFn: async () => {
      const common = { title: form.title ?? '', body: form.body ?? '', terms: form.terms ?? '', expiresAt: new Date(form.expiresAt!).toISOString() };
      if (existing) return api<OfferDto>(`/admin/offers/${existing.id}`, { method: 'PATCH', body: { ...common, paused } });
      const byCampaign = form.trigger === 'CAMPAIGN_CELL';
      return api<OfferDto>('/admin/offers', {
        body: { ...common, brandId: form.brandId, trigger: form.trigger, campaignId: byCampaign ? form.sourceId : null, eventId: byCampaign ? null : form.sourceId },
      });
    },
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: offerKeys.admin });
      toast({ kind: 'success', title: tr('{name} saved', { name: o.title }) });
      close();
      if (!existing) onCreated(o);
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Could not save the offer') }),
  });
  const byCampaign = form.trigger === 'CAMPAIGN_CELL';
  const sources = byCampaign
    ? (campaigns.data ?? []).filter((c) => c.brand.id === form.brandId && c.status !== 'ENDED').map((c) => ({ id: c.id, name: c.name }))
    : (events.data ?? []).filter((e) => !e.finalized && e.status !== 'ENDED').map((e) => ({ id: e.id, name: e.name }));
  const valid = (form.title ?? '').trim().length >= 2 && !!form.expiresAt && (!!existing || (!!form.brandId && !!form.sourceId));
  const set = (k: string) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Sheet open={!!offer} onClose={close} label={t('Offer')}>
      {offer ? (
        <div className="space-y-3" data-testid="offer-sheet">
          <h2 className="font-display text-2xl font-bold">{existing ? t('Edit {name}', { name: existing.title }) : t('New offer')}</h2>
          {existing ? (
            <p className="text-sm text-ink-400">
              {existing.brand.name} · {t(TRIGGER_LABEL[existing.trigger])}: {existing.source?.name ?? '—'}
            </p>
          ) : (
            <>
              <label className="block text-sm font-semibold">
                {t('Brand')}
                <select value={form.brandId} onChange={(e) => setForm({ ...form, brandId: e.target.value, sourceId: '' })} className={input} data-testid="offer-brand">
                  <option value="">{t('Choose a brand…')}</option>
                  {brands.data?.filter((b) => !b.archived).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-semibold">
                {t('Earned by')}
                <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value, sourceId: '' })} className={input} data-testid="offer-trigger">
                  {(Object.keys(TRIGGER_LABEL) as OfferTrigger[]).map((k) => (
                    <option key={k} value={k}>
                      {t(TRIGGER_LABEL[k])}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-semibold">
                {t(byCampaign ? 'Campaign (of this brand)' : 'Event (not ended)')}
                <select value={form.sourceId} onChange={set('sourceId')} className={input} data-testid="offer-source">
                  <option value="">{t('Choose…')}</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          <label className="block text-sm font-semibold">
            {t('Title (what the runner gets)')}
            <input value={form.title ?? ''} onChange={set('title')} className={input} data-testid="offer-title" />
          </label>
          <label className="block text-sm font-semibold">
            {t('Description')}
            <textarea value={form.body ?? ''} onChange={set('body')} rows={2} className={`${input} h-auto py-2`} data-testid="offer-body" />
          </label>
          <label className="block text-sm font-semibold">
            {t('Conditions of use')}
            <textarea value={form.terms ?? ''} onChange={set('terms')} rows={2} className={`${input} h-auto py-2`} data-testid="offer-terms" />
          </label>
          <label className="block text-sm font-semibold">
            {t('Valid until')}
            <input type="datetime-local" value={form.expiresAt ?? ''} onChange={set('expiresAt')} className={input} data-testid="offer-expires" />
          </label>
          {existing ? (
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} className="size-5" />
              {t('Paused (no new codes handed out)')}
            </label>
          ) : null}
          <Button className="w-full" loading={save.isPending} disabled={!valid} onClick={() => save.mutate()} data-testid="offer-save">
            {t(existing ? 'Save' : 'Save, then add codes')}
          </Button>
        </div>
      ) : null}
    </Sheet>
  );
}

/** Upload a CSV (or paste a list): the first column of each line is a code. */
function CodesSheet({ offer, onClose }: { offer: OfferDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const t = useT();
  const [csv, setCsv] = useState('');
  const close = () => {
    setCsv('');
    onClose();
  };
  const add = useMutation({
    mutationFn: () => api<OfferDto & { added: number; skipped: number }>(`/admin/offers/${offer!.id}/codes`, { body: { csv } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: offerKeys.admin });
      toast({ kind: 'success', title: tr('{n} codes added', { n: r.added }), body: r.skipped ? tr('{n} duplicates skipped', { n: r.skipped }) : undefined });
      close();
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Could not add the codes') }),
  });
  const clear = useMutation({
    mutationFn: () => api<OfferDto>(`/admin/offers/${offer!.id}/codes`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: offerKeys.admin });
      toast({ kind: 'success', title: tr('Codes not given yet were removed') });
      close();
    },
  });
  const lines = csv.split(/\r?\n/).filter((l) => l.trim()).length;
  return (
    <Sheet open={!!offer} onClose={close} label={t('Codes')}>
      {offer ? (
        <div className="space-y-3" data-testid="offer-codes-sheet">
          <h2 className="font-display text-2xl font-bold">{t('Codes · {name}', { name: offer.title })}</h2>
          <p className="text-sm text-ink-400">
            {t('{claimed} of {total} given. Upload a CSV file or paste one code per line (first column; a “code” header is skipped).', { claimed: offer.codes.claimed, total: offer.codes.total })}
          </p>
          <label className="cursor-pointer text-sm font-semibold text-volt">
            {t('Choose a CSV file')}
            <input
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              className="hidden"
              data-testid="offer-codes-file"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) setCsv(await f.text());
              }}
            />
          </label>
          <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} placeholder={'CODE-001\nCODE-002'} className={`${input} h-auto py-2 font-mono text-sm`} data-testid="offer-codes-input" />
          <Button className="w-full" loading={add.isPending} disabled={!lines} onClick={() => add.mutate()} data-testid="offer-codes-save">
            {t(!lines ? 'Add codes' : lines === 1 ? 'Add 1 code' : 'Add {n} codes', { n: lines })}
          </Button>
          {offer.codes.total > offer.codes.claimed ? (
            <button className="w-full text-sm font-semibold text-ink-400 hover:text-enemy" onClick={() => clear.mutate()}>
              {t('Remove the {n} codes not given yet', { n: offer.codes.total - offer.codes.claimed })}
            </button>
          ) : null}
        </div>
      ) : null}
    </Sheet>
  );
}
